import type { CaepfEntry } from '@/services';
import { isActiveCaepf } from '@/features/quotes/hooks/common/use-caepf-selection.hook';
import {
  Button,
  Dialog,
  Tag,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { AlertTriangle, Check, IdCard } from 'lucide-react';
import { useEffect, useState } from 'react';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (entry: CaepfEntry) => void;
  /** CAEPFs vinculados ao CPF consultado. */
  items: CaepfEntry[];
};

/** Chave estável de uma linha — a inscrição é única; cai no índice se vier nula. */
function entryKey(entry: CaepfEntry, index: number): string {
  return entry.registrationNumber ?? `caepf-${index}`;
}

/**
 * Seleção de CAEPF quando a consulta por CPF retorna MAIS DE UM CAEPF
 * vinculado ao titular (com um único CAEPF o preenchimento é direto, sem
 * modal — ver `useCaepfSelection`). O documento efetivamente usado na cotação
 * é sempre a INSCRIÇÃO do CAEPF escolhido; o CPF é só o ponto de entrada.
 */
export function SelectCaepfModal({
  isOpen,
  onClose,
  onConfirm,
  items,
}: Props) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // O Dialog mantém o conteúdo montado entre aberturas — limpar a seleção no
  // fechamento evita que a escolha anterior reapareça na próxima consulta.
  useEffect(() => {
    if (!isOpen) setSelectedKey(null);
  }, [isOpen]);

  const selectedIndex = items.findIndex(
    (entry, index) => entryKey(entry, index) === selectedKey
  );
  const selected = selectedIndex >= 0 ? items[selectedIndex] : null;

  // Só CAEPF com situação ATIVA pode ser usado na cotação. Os demais aparecem
  // na lista (para o corretor entender o porquê) mas não são selecionáveis.
  const activeCount = items.filter(entry =>
    isActiveCaepf(entry.registrationStatus)
  ).length;
  const hasSelectable = activeCount > 0;

  const handleConfirm = () => {
    if (!selected || !isActiveCaepf(selected.registrationStatus)) return;
    onConfirm(selected);
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={details => {
        if (!details.open) onClose();
      }}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-[720px] max-w-[90vw] rounded-[24px] bg-[#F9F9F9] p-0 shadow-xl'>
          <div className='flex flex-col gap-4 p-6'>
            <Dialog.Header className='w-full p-0'>
              <Dialog.Title>
                {/* `w-full` é necessário: o Dialog.Title já é flex, então sem
                    isso este wrapper encolhe e o X cola no texto. */}
                <div className='flex w-full items-center justify-between gap-3'>
                  <div className='flex items-center gap-2'>
                    <IdCard className='h-6 w-6 text-alba-black-500' />
                    <Text className='text-lg font-bold text-alba-black-500'>
                      Selecione o CAEPF
                    </Text>
                  </div>
                  <Dialog.CloseButton
                    onClick={onClose}
                    className='text-alba-gray-600'
                  />
                </div>
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body className='w-full p-0'>
              <Text className='text-sm text-alba-gray-600'>
                {`O CPF informado possui ${items.length} CAEPFs na Receita Federal. Escolha qual será usado nesta cotação.`}
                {activeCount < items.length
                  ? ' Apenas CAEPFs com situação ATIVA podem ser utilizados.'
                  : ''}
              </Text>

              <div className='mt-4 flex flex-col gap-3 max-h-[60vh] overflow-y-auto'>
                {items.map((entry, index) => {
                  const key = entryKey(entry, index);
                  const isSelected = key === selectedKey;
                  const active = isActiveCaepf(entry.registrationStatus);

                  return (
                    <button
                      key={key}
                      type='button'
                      onClick={() => setSelectedKey(key)}
                      disabled={!active}
                      aria-pressed={isSelected}
                      className={`flex items-start justify-between gap-4 rounded-lg border p-4 text-left transition-colors ${
                        !active
                          ? 'border-alba-gray-400 opacity-60 cursor-not-allowed'
                          : isSelected
                            ? 'border-alba-blue-600 bg-alba-blue-50 cursor-pointer'
                            : 'border-alba-gray-400 hover:bg-alba-gray-50 cursor-pointer'
                      }`}
                    >
                      <div className='flex flex-col gap-1'>
                        <div className='flex items-center gap-2 flex-wrap'>
                          <Text className='text-base font-bold text-alba-black-500'>
                            {entry.registrationNumber ?? 'Inscrição não informada'}
                          </Text>
                          <Tag.Root
                            size='sm'
                            variant='outline'
                            colorScheme={active ? 'success' : 'error'}
                          >
                            <Tag.Label>
                              {entry.registrationStatus ?? 'SITUAÇÃO DESCONHECIDA'}
                            </Tag.Label>
                          </Tag.Root>
                        </div>
                        {entry.responsibleName && (
                          <Text className='text-sm text-alba-black-500'>
                            {entry.responsibleName}
                          </Text>
                        )}
                        {entry.activityStartDate && (
                          <Text className='text-sm text-alba-gray-600'>
                            Início da atividade: {entry.activityStartDate}
                          </Text>
                        )}
                      </div>
                      {isSelected && (
                        <Check
                          className='h-5 w-5 shrink-0 text-alba-blue-600'
                          strokeWidth={2}
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              {!hasSelectable && (
                <div className='mt-4 flex items-start gap-2 rounded-md border border-alba-error-red-300 bg-alba-error-red-50 p-4 text-alba-error-red-800'>
                  <AlertTriangle
                    className='mt-0.5 h-5 w-5 shrink-0'
                    strokeWidth={1.8}
                  />
                  <Text className='text-sm'>
                    Nenhum dos CAEPFs vinculados a este CPF está com situação
                    ATIVA na Receita Federal. Confirme a regularização com o
                    cliente ou informe outro documento.
                  </Text>
                </div>
              )}
            </Dialog.Body>

            <Dialog.Footer className='w-full p-0'>
              <div className='flex w-full items-center justify-end gap-3'>
                <Button variant='ghost' size='large' onClick={onClose}>
                  Cancelar
                </Button>
                <Button
                  variant='primary'
                  size='large'
                  onClick={handleConfirm}
                  disabled={
                    !selected || !isActiveCaepf(selected.registrationStatus)
                  }
                >
                  Selecionar
                </Button>
              </div>
            </Dialog.Footer>
          </div>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
